import { apiGet } from '../../../adapters/api-client/api';
import type { DisplayTimeZone, JoinPolicy, JoinWindow, JoinWindowState } from './learner';

/**
 * UC-P08 — the household calendar (backend:
 * booking/application/learner-schedule.service.ts `householdCalendarFor`).
 *
 * The same envelope as one learner's calendar — the governed join policy, the
 * server clock and the display zone — with every event naming its learner and
 * enrollment. No price, no payment state.
 */

export type CalendarEventState = 'Confirmed' | 'Rescheduled' | 'Cancelled' | 'Completed' | 'NoShow';

export interface HouseholdCalendarEvent {
  bookingId: string;
  enrollmentId: string;
  seq: number;
  state: CalendarEventState;
  /** Why a Cancelled event is cancelled: the session alone, or its whole enrollment. */
  cancelledWith?: 'SESSION' | 'ENROLLMENT';
  scheduledStart: string;
  scheduledEnd?: string;
  /** A pending reschedule's proposed slot; the class stays at `scheduledStart` until accepted. */
  proposedStart?: string;
  proposedEnd?: string;
  courseId: string;
  courseTitle?: string;
  teacherName?: string;
  studentId: string;
  learnerName?: string;
  /** Present only while the session can still be entered. */
  joinWindow?: JoinWindow;
  joinState?: JoinWindowState;
}

export interface HouseholdCalendarResponse {
  joinPolicy: JoinPolicy;
  serverNow: string;
  timeZone: DisplayTimeZone;
  calendar: HouseholdCalendarEvent[];
  /**
   * True when the server could not read every enrollment's sessions: the
   * calendar is a partial one and must not be shown as the whole week.
   */
  incomplete?: boolean;
  /** Whose classes may be missing — names only, never an id. */
  incompleteLearners?: string[];
}

/** GET /api/v1/me/calendar?from=&to= — the parent's whole household, bounded. */
export function getHouseholdCalendar(from: string, to: string): Promise<HouseholdCalendarResponse> {
  const params = new URLSearchParams({ from, to });
  return apiGet(`/api/v1/me/calendar?${params.toString()}`);
}
